import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { WebSocketContext } from "../../WebSocket";
import { path } from "../../path";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import { useSelector } from "react-redux";

const VideoUploadTester = () => {
  const [files, setFiles] = useState([]);
  const [statusBlocks, setStatusBlocks] = useState([]);
  const [savedUploads, setSavedUploads] = useState([]);
  const [backendStatus, setBackendStatus] = useState("Checking...");
  const [socketStatus, setSocketStatus] = useState("Connecting...");
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [mediaType, setMediaType] = useState("video"); // "video" or "image"
  
  const socket = useContext(WebSocketContext);
  const token = useSelector((state) => state.AuthReducer.token);
  const fileInputRef = useRef(null);

  const axiosInstance = useMemo(() => {
    return axios.create({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const getOrCreateUserSocketId = () => {
    let storedId = localStorage.getItem("testUserId");
    if (!storedId) {
      storedId = `user-${Date.now()}-${Math.random() * 10000}`;
      localStorage.setItem("testUserId", storedId);
    }
    return storedId;
  };
  const userId = useRef(getOrCreateUserSocketId());

  const calculateVideoUploadChunkSize = async () => {
    const dummySize = 512 * 1024; // 512 KB
    const dummyData = new Uint8Array(dummySize);
    const blob = new Blob([dummyData]);

    const startTime = performance.now();
    await axios.post(`${path}/upload-speed-test`, blob, { 
      headers: {
        "Content-Type": "application/octet-stream"
      } 
    });
    const endTime = performance.now();

    const durationSeconds = (endTime - startTime) / 1000;
    const speedBps = dummySize / durationSeconds;
    const speedMbps = (speedBps * 8) / (1024 * 1024);
    
    let chunkSize;
    if (speedMbps < 1) {
      chunkSize = 100 * 1024;  // 100 KB for very poor connections
    } else if (speedMbps < 5) {
      chunkSize = 2 * 1024 * 1024;  // 2 MB for moderate 3G/slow 4G
    } else {
      chunkSize = 5 * 1024 * 1024;  // 5 MB for fast 4G/5G/WiFi
    }
    return { chunkSize, speedMbps: speedMbps.toFixed(1) };
  };

  useEffect(() => {
    socket.emit("test-user-join", userId.current);
    socket.on("connect", () => setSocketStatus("🟢 Connected"));
    socket.on("disconnect", () => setSocketStatus("🔴 Disconnected"));

    renderSavedUploads();
    checkBackendStatus();
    const interval = setInterval(checkBackendStatus, 15000);

    return () => {
      clearInterval(interval);
      socket.emit("test-user-leave", userId.current);
      socket.disconnect();
    };
  }, []);

  const checkBackendStatus = async () => {
    try {
      await axios.get(`${path}/ping`);
      setBackendStatus("🟢 Connected");
    } catch {
      setBackendStatus("🔴 Disconnected");
    }
  };

  const handleFileChange = (e) => setFiles([...e.target.files]);

  const getAccessToken = async () => {
    if(accessToken) {
      return accessToken;
    } else {
      const res = await axiosInstance.get(`${path}/test/access-token`);
      setAccessToken(res.data.accessToken);
      return res.data.accessToken;
    }
  };

  const saveToLocalStorage = (fileData) => {
    let current = JSON.parse(localStorage.getItem("uploads") || "[]");
    const save = current.find((data) => data.id === fileData.id);
    if (save) {
      current = current.map((data) => {
        if (data.id === fileData.id) {
          return fileData;
        }
        return data;
      });
    } else {
      current.push(fileData);
    }
    localStorage.setItem("uploads", JSON.stringify(current));
    renderSavedUploads();
  };

  const renderSavedUploads = async () => {
    const uploads = JSON.parse(localStorage.getItem("uploads") || "[]");

    const updatedUploads = await Promise.all(
      uploads.map(async (upload) => {
        // Skip if already has size and URL
        if (upload.new_file_size && upload.url) {
          return upload;
        }

        if (upload.url) {
          try {
            const res = await axiosInstance.get(`${path}/test/file-info/${upload.id}`);
            upload.new_file_size = res.data.fileSize;
          } catch (err) {
            console.error("Failed to fetch file size for", upload.url, err);
          }
        }

        return upload;
      })
    );

    setSavedUploads(updatedUploads);
  };

  const deleteFromLocalStorage = async (id) => {
    try {
      const res = await axiosInstance.delete(`${path}/test/delete-file/${id}`);
      if (res.status === 200) {
        const current = JSON.parse(localStorage.getItem("uploads") || "[]");
        const updated = current.filter((f) => f.id !== id);
        localStorage.setItem("uploads", JSON.stringify(updated));
        renderSavedUploads();
      }
    } catch {
      alert("Failed to delete file from Box.");
    }
  };

  const clearLocalStorage = () => {
    const uploads = JSON.parse(localStorage.getItem("uploads") || "[]");
    uploads.forEach((upload) => deleteFromLocalStorage(upload.id));
  };

  const getVideoDurationInSeconds = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => reject("Error loading video metadata.");
      video.src = URL.createObjectURL(file);
    });
  };

  const readChunkAsBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const DirectSingleUpload = async (
    file,
    fileName,
    status
  ) => {
    try {
      const { speedMbps } = await calculateVideoUploadChunkSize();
      const accessToken = await getAccessToken();
      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("parent_id", "0");

      const uploadStart = Date.now();

      const res = await axios.post(
        "https://upload.box.com/api/2.0/files/content",
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            updateStatus(status.id, `📦 Upload: ${percent}%`);
          },
        }
      );

      const uploadTime = ((Date.now() - uploadStart) / 1000).toFixed(2);
      const fileData = res.data.entries[0];
      saveToLocalStorage({
        id: fileData.id,
        name: fileName,
        upload_time: uploadTime,
        compress_time: "0",
        total_time: uploadTime,
        type: status.type,
        upload_speed: speedMbps,
        original_file_size: (file.size / (1024 * 1024)).toFixed(2),
      });

      const urlData = await generateUrl(fileData.id, fileName);

      const data = {
        id: fileData.id,
        name: fileName,
        url: urlData.url,
        upload_time: uploadTime,
        compress_time: "0",
        total_time: uploadTime,
        upload_speed: speedMbps,
        type: status.type,
        original_file_size: (file.size / (1024 * 1024)).toFixed(2)
      };

      saveToLocalStorage(data);
      updateStatus(status.id, `✅ Uploaded in ${uploadTime}s`);

      try {
        await axiosInstance.post(`${path}/test/compress`, {
          fileArr: [{ fileId: fileData.id, type: mediaType }],
        });
        updateStatus(status.id, `✅ Uploaded in ${uploadTime}s and ✅ Scheduled compression worker`);
      } catch (error) {
        updateStatus(status.id, `✅ Uploaded in ${uploadTime}s and ❌ Scheduling compression worker failed`);
      }
    } catch (err) {
      updateStatus(status.id, `❌ Upload failed`);
    }
  };

  const DirectChunkUpload = async (file, fileName, size, status) => {
    try {
      const { speedMbps } = await calculateVideoUploadChunkSize();
      const accessToken = await getAccessToken();

      const uploadStartTime = Date.now();

      const { data: session } = await axios.post(
        "https://upload.box.com/api/2.0/files/upload_sessions",
        {
          folder_id: "0",
          file_size: size,
          file_name: fileName,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { id: sessionId, part_size: partSize } = session;

      const parts = [];
      let offset = 0;
      const overallSha1 = CryptoJS.algo.SHA1.create();

      while (offset < size) {
        const chunkBlob = file.slice(offset, offset + partSize);
        const base64 = await readChunkAsBase64(chunkBlob);
        const buffer = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        
        overallSha1.update(CryptoJS.enc.Base64.parse(base64));
        const sha1Digest = await calculateSha1Digest(buffer);

        const { data: partResponse } = await axios.put(
          `https://upload.box.com/api/2.0/files/upload_sessions/${sessionId}`,
          buffer,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/octet-stream",
              "Content-Range": `bytes ${offset}-${offset + buffer.byteLength - 1}/${size}`,
              Digest: `sha=${sha1Digest}`,
            },
            onUploadProgress: (progressEvent) => {
              const progress = (((offset + progressEvent.loaded) / size) * 100).toFixed(2);
              updateStatus(status.id, `📦 Upload: ${progress}%`);
            },
          }
        );

        parts.push(partResponse.part);
        offset += buffer.byteLength;
      }

      const fullSha = overallSha1.finalize().toString(CryptoJS.enc.Base64);

      const { data: commitResponse } = await axios.post(
        `https://upload.box.com/api/2.0/files/upload_sessions/${sessionId}/commit`,
        { parts },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            Digest: `sha=${fullSha}`,
          },
        }
      );

      const File = commitResponse.entries[0];
      const uploadTime = ((Date.now() - uploadStartTime) / 1000).toFixed(2);
      const urlData = await generateUrl(File.id, fileName);
      
      const data = {
        id: File.id,
        name: fileName,
        url: urlData.url,
        upload_time: uploadTime,
        compress_time: "0",
        total_time: uploadTime,
        upload_speed: speedMbps,
        type: status.type,
        original_file_size: (file.size / (1024 * 1024)).toFixed(2)
      };
      saveToLocalStorage(data);
      updateStatus(status.id, `✅ Uploaded in ${uploadTime}s`);
      
      try {
        await axiosInstance.post(`${path}/test/compress`, {
          fileArr: [{ fileId: File.id, type: mediaType }],
        });
        updateStatus(status.id, `✅ Uploaded in ${uploadTime}s and ✅ Scheduled compression worker`);
      } catch (err) {
        updateStatus(status.id, `✅ Uploaded in ${uploadTime}s and ❌ Scheduling compression worker failed`);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      throw error;
    }
  };

  const calculateSha1Digest = async (chunk) => {
    const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(chunk));
    const hash = CryptoJS.SHA1(wordArray).toString(CryptoJS.enc.Base64);

    return hash;
  };

  const generateFileName = (file, mediaType, prefix = "File") => {
    const timestamp = Date.now();
    const ext = mediaType === "image" ? ".jpeg" : ".mp4";

    // Remove existing extension and non-word characters
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, ""); // strips `.xxx`
    const safeBaseName = nameWithoutExt.replace(/\W+/g, ""); // strips special chars

    return `${prefix}_${timestamp}_${safeBaseName}${ext}`;
  };

  const DirectBoxUpload = async (
    file,
    fileName,
    status,
  ) => {
    const limit = 50 * 1024 * 1024; // 50 MB in bytes

    try {
      if (file.size < limit) {
        return DirectSingleUpload(file, fileName, status);
      } else {
        return DirectChunkUpload(file, fileName, file.size, status);
      }
    } catch (error) {
      console.log("Error getting file stats:", JSON.stringify(error));
      throw new Error("Error during box upload: "+ error);
    }
  }

  const handleBoxUpload = async () => {
    setLoading(true);

    try {
      if (!files.length) {
        return alert("Select atleast 1 file.");
      }
  
      setStatusBlocks([]);
  
      await Promise.all(
        files.map(async (file) => {
          const name = generateFileName(file, mediaType, "BoxDirect");;
          const status = {
            id: statusBlocks.length+Math.random()*1000,
            name: file.name,
            message: "📦 Upload: 0%",
            type: mediaType
          };
          setStatusBlocks((prev) => [...prev, status]);
  
          await DirectBoxUpload(file, name, status);
        })
      )
    } catch (error) {
      toast.error(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChunkedUpload = async (newOption = false) => {
    setLoading(true);

    try {
      if (!files.length) {
        return alert("Select at least one file.");
      }
      setStatusBlocks([]);
      
      await Promise.all(
        files.map(async (file) => {
          const { chunkSize, speedMbps } = await calculateVideoUploadChunkSize()
          const name = generateFileName(file, mediaType, "Feedants_Test");
          const status = {
            id: statusBlocks.length+Math.random()*1000,
            name: file.name,
            message: "📤 Chunk: 0%",
            type: mediaType
          };
          setStatusBlocks((prev) => [...prev, status]);
  
          const totalChunks = Math.ceil(file.size / chunkSize);
          let uploaded = 0;
          const fullStart = Date.now();
          const uploadStart = Date.now();
  
          const compressionListener = (data) => {
            if (
              data.userSocketId?.userId === userId.current &&
              data.filename === name
            ) {
              updateStatus(status.id, `🌀 Compression: ${data.percentage}%`);
              if (data.percentage >= 100) {
                socket.off("subpercentage", compressionListener);
              }
            }
          };
  
          socket.on("subpercentage", compressionListener);
  
          for (let i = 0; i < totalChunks; i++) {
            const chunkBlob = file.slice(i * chunkSize, (i + 1) * chunkSize);
            const chunkBase64 = await readChunkAsBase64(chunkBlob);
            try {
              await axiosInstance.post(`${path}/test/chunk-upload`, {
                chunk: chunkBase64,
                index: i,
                fileId: name,
              });
              uploaded++;
              const percent = Math.round((uploaded / totalChunks) * 100);
              updateStatus(status.id, `📤 Chunk: ${percent}%`);
            } catch {
              updateStatus(status.id, `❌ Chunk upload failed`);
              return;
            }
          }
  
          const uploadTime = ((Date.now() - uploadStart) / 1000).toFixed(2);
          const compressStart = Date.now();
  
          try {
            const videoLen = await getVideoDurationInSeconds(file);
            const res = await axiosInstance.post(`${path}/test/upload-video`, {
              filename: name,
              userId: userId.current,
              videoLen,
              newOption
            });
  
            const compressTime = ((Date.now() - compressStart) / 1000).toFixed(2);
            const totalTime = ((Date.now() - fullStart) / 1000).toFixed(2);
  
            const data = {
              id: res.data.file_id,
              name: name + (newOption? " new encoding": " old encoding"),
              url: res.data.file_url,
              upload_time: uploadTime,
              compress_time: compressTime,
              total_time: totalTime,
              upload_speed: speedMbps,
              type: mediaType,
              original_file_size: (file.size / (1024 * 1024)).toFixed(2),
              new_file_size: res.data.file_size
            };
  
            saveToLocalStorage(data);
            updateStatus(
              status.id,
              `✅ Uploaded in ${uploadTime}s | Compressed in ${compressTime}s | Total ${totalTime}s`
            );
          } catch {
            updateStatus(status.id, `❌ Compression failed`);
          }
        })
      );
    } catch (error) {
      toast.error(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = (id, message) => {
    setStatusBlocks((prev) =>
      prev.map((block) => (block.id === id ? { ...block, message } : block))
    );
  };

  const handleMediaChange = (e) => {
    setMediaType((prev) => {
      if(prev !== e.target.value) {
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = ""; // Clear file input value
        }
      }
      return e.target.value
    })
  }

  const generateUrl = async (id, name) => {
    // API endpoint for shared links here
    const res = await axiosInstance.post(`${path}/test/generate-url`, { id, name });
    return res.data;
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-6 text-gray-800 font-sans">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6 space-y-6">
        <h1 className="text-2xl font-bold text-center">
          📹 Upload Video (Box / Chunked + Compression)
        </h1>

        <div className="text-sm space-y-1">
          <div>
            <strong>Backend:</strong> <span>{backendStatus}</span>
          </div>
          <div>
            <strong>Socket:</strong> <span>{socketStatus}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="font-medium mr-2">Select Media Type:</label>
            <select
              disabled={loading}
              value={mediaType}
              onChange={handleMediaChange}
              className="border px-2 py-1 rounded text-sm"
            >
              <option value="video">🎥 Video</option>
              <option value="image">🖼️ Image</option>
            </select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={mediaType === "video" ? "video/*" : "image/*"}
            multiple
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0 file:text-sm file:font-semibold
            file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleBoxUpload}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            {loading? 
              <div className="border-2 border-t-white border-teal-400 size-5 rounded-full animate-spin" />
              :"📦 Upload Direct to Box"}
          </button>
          {mediaType === "video" && <>
            <button
              onClick={() => handleChunkedUpload(false)}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              {loading? 
                <div className="border-2 border-t-white border-teal-400 size-5 rounded-full animate-spin" />
                :"📚 Upload Via Backend"}
            </button>
            <button
              onClick={() => handleChunkedUpload(true)}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              {loading? 
                <div className="border-2 border-t-white border-teal-400 size-5 rounded-full animate-spin" />
                :"📚 Upload Via Backend (new encoding)"}
            </button>
          </>}
          <button
            onClick={clearLocalStorage}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
            🗑️ Delete All Files
          </button>
        </div>

        <div>
          <h2 className="text-xl font-semibold mt-4">📈 Live Upload Status</h2>
          <div className="space-y-3 mt-2">
            {statusBlocks.map((block) => (
              <div key={block.id} className="border rounded p-3 bg-gray-50">
                <p className="font-medium">{block.name}</p>
                <p className="text-green-700 font-semibold">{block.message}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mt-6">💾 Saved Uploads</h2>
          {savedUploads.length === 0 ? (
            <p className="text-gray-500 mt-2">No saved uploads yet.</p>
          ) : (
            <div className="space-y-4 mt-2">
              {savedUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="border rounded p-4 bg-white shadow-sm">
                  <p className="font-bold text-gray-800">{upload.name}</p>
                  <p className="text-sm">Upload Time: {upload.upload_time}s</p>
                  <p className="text-sm">
                    Compression Time: {upload.compress_time}s
                  </p>
                  <p className="text-sm">Total Time: {upload.total_time}s</p>
                  <p className="text-sm">Upload Speed: {upload.upload_speed} Mbps</p>
                  <p className="text-sm">Original File Size: {upload.original_file_size} MB</p>
                  {upload?.new_file_size && <p className="text-sm">New File Size: {upload.new_file_size} MB</p>}
                  <p className="text-sm">Box Id: {upload.id}</p>
                  <p className="text-sm break-all">
                    🔗{" "}
                    <a
                      href={upload.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline">
                      {upload.url}
                    </a>
                  </p>
                  {upload.type === "video" ?
                    (
                      <video controls>
                        <source src={upload.url} ></source>
                      </video>
                    ) : (
                      <img src={upload.url} alt={upload.name+" image"} />
                    )
                  }
                  <div className="mt-2">
                    <button
                      onClick={() => deleteFromLocalStorage(upload.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm">
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUploadTester;
